<template>
    <span
        v-if="reviewRating"
        :class="
            [
                'RatingCount',
                { 'RatingCount--clickable': clickable },
            ]
        "
        aria-label="Rating: {{ formattedRating }} stars, based on {{ formattedCount }} reviews"
    >
        <b
            v-skeleton="{
                loading,
                sizes: {
                    Small: { width: '1.125rem', height: '0.75rem' },
                }
            }"
        >
            {{ formattedRating }}
        </b>
        <i
            v-skeleton="{
                loading,
                sizes: {
                    Small: { width: '3rem', height: '0.75rem' },
                },
                margins: {
                    Small: '0 0.25rem',
                }
            }"
            class="lnr-star-full"
            aria-hidden="true"
        ></i>

        <template
            v-if="!loading"
        >
            <span
                v-if="!clickable"
                class="Count"
            >({{ formattedCount }})</span>

            <span
                v-else
                class="Count"
            >{{ formattedCount }} Reviews</span>
        </template>
    </span>
</template>

<script setup lang="ts">
import {IReviewRating} from "@app/interfaces/dtos/IReviewRating";

const props = defineProps({
    reviewRating: {
        type: Object as PropType<IReviewRating | null>,
        default: null,
    },
    loading: {
        type: Boolean,
        default: false,
    },
    clickable: {
        type: Boolean,
        default: false,
    },
});

import { computed, PropType } from 'vue';
import { formatCount } from '@utils/formatCount';

const formattedRating = computed(() => {
    if (!props.reviewRating) return '';

    const formatter = new Intl.NumberFormat('de-DE', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    });
    return formatter.format(props.reviewRating.rating);
});

const formattedCount = computed(() => {
    return formatCount(props.reviewRating.amount);
});

</script>

<style scoped lang="scss">
    .RatingCount {
        display: flex;
        color: $Grey-700;

        span.Count, i.lnr-star-full {
            @include TextStyle(UiSmallRegular);
        }

        b {
            color: $Grey-900;

            @include TextStyle(UiSmallSemibold);
        }

        i {
            display: inline-flex;
            color: $Yellow-900;
            min-width: .8125rem;

            @include MarginAreas(0, 0.25);
        }

        &--clickable {
            b {
                color: $Grey-700;
            }

            span.Count {
                color: $Blue-700;
                cursor: pointer;

                @include TextStyle(UiSmallSemibold);

                &:hover {
                    text-decoration: underline;
                }
            }
        }
    }
</style>
